import { FC, ComponentProps } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedPrice } from 'dibs-buyer-price';
import { Badge } from 'dibs-elements/exports/Badge';
import { useShippingPrice } from '../../helpers/useShippingPrice';
import {
    DEFAULT_SHIPPING_BADGE_TYPE,
    FREE_SHIPPING,
    FREE_SHIPPING_AND_COST,
} from '../../constants';
import classnames from 'classnames';

import { Shipping_item$data as ItemProp } from './__generated__/Shipping_item.graphql';
import { Shipping_viewer$data as ViewerProp } from './__generated__/Shipping_viewer.graphql';

import styles from './styles.scss';

type BadgeBackgroundColor = ComponentProps<typeof Badge>['backgroundColor'];

type FreeShippingProps = React.HTMLAttributes<HTMLDivElement> & {
    backgroundColor?: BadgeBackgroundColor;
};

export const FreeShippingBadge: FC<FreeShippingProps> = ({ backgroundColor, ...props }) => {
    return (
        <div
            {...props}
            className={classnames(styles.freeContainer, props.className)}
            data-tn="free-shipping"
        >
            <Badge type="pill" size="small-heavy" backgroundColor={backgroundColor}>
                <FormattedMessage
                    id="dc.searchProductTile.freeShipping"
                    defaultMessage="Free Shipping"
                />
            </Badge>
        </div>
    );
};

const ShippingComponent: FC<{
    viewer: ViewerProp | null;
    item: ItemProp | null;
    currency: string;
    //when using FREE_SHIPPING_AND_COST type we need to provide buyerId, fetchShippingCostInfo variables to relay fragment
    type?: typeof FREE_SHIPPING | typeof FREE_SHIPPING_AND_COST;
    backgroundColor?: BadgeBackgroundColor;
}> = ({ item, viewer, currency, type = DEFAULT_SHIPPING_BADGE_TYPE, backgroundColor }) => {
    const amount = useShippingPrice({ item, viewer, currency, type });

    if (amount === null) {
        return null;
    }

    if (amount === 0) {
        return <FreeShippingBadge backgroundColor={backgroundColor} />;
    }

    return (
        <div className={styles.costContainer} data-tn="shipping-cost">
            <FormattedMessage
                id="dc.searchProductTile.shipping"
                defaultMessage="{price} shipping"
                values={{ price: <FormattedPrice currency={currency} value={amount} /> }}
            />
        </div>
    );
};

export const Shipping = createFragmentContainer(ShippingComponent, {
    viewer: graphql`
        fragment Shipping_viewer on Viewer
        @argumentDefinitions(
            fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            userZipCode: { type: "String", defaultValue: "" }
            userCountryCode: { type: "String", defaultValue: "" }
        ) {
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment Shipping_item on Item
        @argumentDefinitions(
            buyerId: { type: "String", defaultValue: "" }
            fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
            fetchLiveShipmentQuote: { type: "Boolean", defaultValue: true }
        ) {
            ...useShippingPrice_item
                @arguments(
                    buyerId: $buyerId
                    fetchShippingCostInfo: $fetchShippingCostInfo
                    fetchLiveShipmentQuote: $fetchLiveShipmentQuote
                )
        }
    `,
});
